import { CancelOutlined, FileDownloadOutlined } from "@mui/icons-material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { Menu, MenuItem } from "@mui/material";
import { FC, useState } from "react";
import { MuiBox, MuiButtonTertiary } from "../../../../components";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { BULK_TYPE, SummaryStatus } from "../../../../helpers/constants";
import { RecordStats } from "../../../../helpers/types/common";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  SUMMARY_CARD_TYPE,
  SummaryCard,
} from "../../../createBulkActivity/components/summaryCard/SummaryCard";
import { DownloadParamType, DownloadType } from "../../modelsAndSchema/Models";
import { bulkCreateProgramSiteCoreKeys } from "../../siteCore/siteCoreKeys";
import {
  BulkCardsWrapper,
  BulkReportWrapper,
  HorizontalLine,
  VerticalDivider,
} from "./BulkProgramStatusPanel.style";

export interface BulkProgramStatusPanelProps {
  recordStats: RecordStats;
  processName: string;
  status: string;
  onRefresh?: () => void;
  onCancel?: () => void;
  onDownload?: (type: DownloadType) => void;
  onDownloadUploadedFile?: () => void;
}

export const BulkProgramStatusPanel: FC<BulkProgramStatusPanelProps> = ({
  status,
  recordStats,
  processName,
  onRefresh,
  onCancel,
  onDownload,
  onDownloadUploadedFile,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { useFetchLabel } = useSiteCore();

  const {
    summaryComponent,
    refreshStatusLink,
    downloadReportCTAButton,
    processedProgramsOption,
    unprocessedProgramsOption,
    cancelBulkProcessCTAButton,
    downloadUploadedFileCTAButton,
  } = bulkCreateProgramSiteCoreKeys;

  const getLabel = useFetchLabel(summaryComponent);

  const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleDownload = (type: DownloadType) => {
    onDownload?.(type);
    handleMenuClose();
  };

  return (
    <BulkReportWrapper>
      <MuiBox>
        <MuiEllipseLabel
          text={processName}
          labelTypoVariant="h3Bold"
          labelTypoClassName="label-name-value"
        />
      </MuiBox>

      <BulkCardsWrapper>
        <SummaryCard
          type={SUMMARY_CARD_TYPE.totalNumberOfPrograms}
          count={recordStats.total.count}
          label={recordStats.total.label}
          bulkType={BULK_TYPE.PROGRAM}
        />
        <VerticalDivider />
        <SummaryCard
          type={SUMMARY_CARD_TYPE.processed}
          count={recordStats.processed.count}
          label={recordStats.processed.label}
          bulkType={BULK_TYPE.PROGRAM}
        />

        {recordStats.unprocessed && (
          <>
            <VerticalDivider />
            <SummaryCard
              type={SUMMARY_CARD_TYPE.unprocessed}
              count={recordStats.unprocessed.count}
              label={recordStats.unprocessed.label}
              bulkType={BULK_TYPE.PROGRAM}
            />
          </>
        )}
      </BulkCardsWrapper>
      <HorizontalLine />
      {status === SummaryStatus.PROCESSING && onRefresh && (
        <MuiButtonTertiary
          data-testid={"summary-button-testId"}
          onClick={onRefresh}
        >
          <RefreshOutlinedIcon />
          {getLabel(refreshStatusLink)}
        </MuiButtonTertiary>
      )}

      {status === SummaryStatus.QUEUE && (
        <MuiBox className="queue-buttons">
          {onCancel && (
            <MuiButtonTertiary
              onClick={onCancel}
              data-testid="cancel-bulk-process"
            >
              <CancelOutlined />
              {getLabel(cancelBulkProcessCTAButton)}
            </MuiButtonTertiary>
          )}

          {onDownloadUploadedFile && (
            <MuiButtonTertiary
              onClick={onDownloadUploadedFile}
              data-testid="download-uploaded-file"
            >
              <FileDownloadOutlined />
              {getLabel(downloadUploadedFileCTAButton)}
            </MuiButtonTertiary>
          )}
        </MuiBox>
      )}

      {status === SummaryStatus.COMPLETED && onDownload && (
        <>
          <MuiButtonTertiary
            onClick={handleDownloadClick}
            data-testid="download-report"
          >
            <FileDownloadOutlined />
            {getLabel(downloadReportCTAButton)}
          </MuiButtonTertiary>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            {recordStats.processed?.count ? (
              <MenuItem
                onClick={() =>
                  handleDownload(
                    DownloadParamType.PROCESSED_REPORT as DownloadType,
                  )
                }
              >
                {getLabel(processedProgramsOption)}
              </MenuItem>
            ) : null}
            {recordStats.unprocessed?.count ? (
              <MenuItem
                onClick={() =>
                  handleDownload(
                    DownloadParamType.UNPROCESSED_REPORT as DownloadType,
                  )
                }
              >
                {getLabel(unprocessedProgramsOption)}
              </MenuItem>
            ) : null}
          </Menu>
        </>
      )}
    </BulkReportWrapper>
  );
};
